import type { Incident, Target } from '@/types/map'
import '../map-console.css'

export interface TopSectorsPanelProps {
  targets: Target[]
  incidents: Incident[]
  /**
   * Fires with the sector name on click. MapNextPage wires this to the
   * sector filter (toggle behaviour handled at the consumer).
   */
  onSectorClick: (sector: string) => void
  /**
   * Optional active-filter set — used purely for visual feedback
   * (aria-pressed + bar opacity). Defaults to an empty set.
   */
  activeSectors?: Set<string>
  isLoading?: boolean
}

const MAX_ROWS = 6

export function TopSectorsPanel({
  targets,
  incidents,
  onSectorClick,
  activeSectors = new Set<string>(),
  isLoading = false,
}: TopSectorsPanelProps) {
  if (isLoading) {
    return (
      <section
        aria-busy="true"
        data-testid="top-sectors-skeleton"
        className="soc-panel p-3"
      >
        <div className="mb-2 h-3 w-20 animate-pulse rounded bg-[color:var(--surface-tertiary)]" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-4 w-full animate-pulse rounded bg-[color:var(--surface-tertiary)]"
            />
          ))}
        </div>
      </section>
    )
  }

  const sectorByTarget = new Map<string, string>()
  for (const t of targets) sectorByTarget.set(t.id, t.sector)

  const counts = new Map<string, number>()
  for (const inc of incidents) {
    const sec = sectorByTarget.get(inc.targetId)
    if (!sec) continue
    counts.set(sec, (counts.get(sec) ?? 0) + 1)
  }

  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1])
  const top = ranked.slice(0, MAX_ROWS)
  const max = top[0]?.[1] ?? 1

  return (
    <section className="soc-panel overflow-hidden">
      <header className="soc-panel-head flex items-center justify-between px-3 py-2">
        <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          <span className="text-[color:var(--interactive-primary)]" aria-hidden>
            ▸
          </span>
          Targeted Sectors
        </h3>
        {top.length > 0 && (
          <span className="font-mono text-[10px] tracking-widest text-[color:var(--text-tertiary)]">
            24H
          </span>
        )}
      </header>
      {top.length === 0 ? (
        <p
          data-testid="top-sectors-empty"
          className="px-3 py-3 text-xs text-[color:var(--text-tertiary)]"
        >
          No sectors targeted in range
        </p>
      ) : (
        <ul className="px-2 py-1.5">
          {top.map(([sector, count]) => {
            const active = activeSectors.has(sector)
            return (
              <li
                key={sector}
                data-testid="sector-row"
                aria-pressed={active}
                role="button"
                tabIndex={0}
                onClick={() => onSectorClick(sector)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSectorClick(sector)
                  }
                }}
                className="group mb-0.5 cursor-pointer rounded-md px-1.5 py-1 text-xs last:mb-0 transition-colors hover:bg-[color:var(--surface-tertiary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-primary)]"
              >
                <div className="flex justify-between">
                  <span className="truncate capitalize text-[color:var(--text-primary)] group-hover:text-[color:var(--interactive-primary)]">
                    {sector}
                  </span>
                  <span className="font-mono text-[color:var(--text-secondary)]">{count}</span>
                </div>
                <div
                  role="presentation"
                  className="mt-1 h-1.5 overflow-hidden rounded-full bg-[color:var(--surface-tertiary)]"
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-200"
                    style={{
                      width: `${Math.max((count / max) * 100, 4)}%`,
                      background: active
                        ? 'var(--interactive-primary)'
                        : 'var(--interactive-secondary, var(--interactive-primary))',
                      opacity: activeSectors.size === 0 || active ? 1 : 0.4,
                    }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
